<template>
  <TabList
    class="border-outline-variant relative w-full flex-row items-center justify-items-stretch transition-colors"
  >
    <BaseHorizontalOverflow v-slot="{ hasOverflow }" :active-html="activeTabElement">
      <div
        ref="container"
        class="relative flex w-max flex-row gap-0.5"
        :class="[hasOverflow && 'pl-[52px]']"
      >
        <Tab
          v-for="item in tabItems"
          :key="item.id"
          v-slot="{ selected }"
          as="template"
          @click="elementClicked"
        >
          <BaseTabButton
            :selected="selected"
            :img="item.img"
            :icon="item.icon"
            :scrollable="hasOverflow || tabItems.length < 2"
            :badge-count="item.badgeCount"
            >{{ item.title }}</BaseTabButton
          >
        </Tab>
        <BaseTabIndicator :active-element="activeTabElement" />
      </div>
    </BaseHorizontalOverflow>
  </TabList>
</template>
<script setup lang="ts">
  import { Tab, TabList } from '@headlessui/vue'
  import { computed, inject, onMounted, ref, watch } from 'vue'
  import BaseHorizontalOverflow from '../horizontal-overflow/BaseHorizontalOverflow.vue'
  import BaseTabButton from './BaseTabButton.vue'
  import BaseTabIndicator from './BaseTabIndicator.vue'
  import { tabBarGroupKey, type TabBarItem } from './use-tab-bar-query'

  interface Props {
    tabItems: TabBarItem[]
    selectedIndex?: number
  }

  const props = defineProps<Props>()

  const injected = inject(tabBarGroupKey, null)

  const activeIndex = computed(() => injected?.selectedIndex.value ?? props.selectedIndex)

  const activeTabElement = ref<HTMLElement>()

  const elementClicked = (event: Event) => {
    activateElement(event.target as HTMLElement)
  }

  const activateElement = (element?: HTMLElement) => {
    if (element) {
      activeTabElement.value = element.closest('button') ?? undefined
    }
  }

  const container = ref<HTMLDivElement | null>(null)

  onMounted(() => {
    if (!container.value) return
    const index = activeIndex.value ?? 0
    activateElement((container.value.children[index] as HTMLElement) ?? undefined)
  })

  watch(activeIndex, (newValue) => {
    if (newValue != undefined && container.value) {
      activateElement((container.value.children[newValue] as HTMLElement) ?? undefined)
    }
  })
</script>
